import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelopeSquare, faInfo, faInfoCircle, faKey } from '@fortawesome/free-solid-svg-icons';
import './Login.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useRef } from 'react';
import {
  useCreateUserWithEmailAndPassword,
  useSignInWithEmailAndPassword,
  useSendPasswordResetEmail
} from 'react-firebase-hooks/auth';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { db, auth, sendEmailVerification } from '../../../firebase.init';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import logo from "../../Assets/logo.png";

const Login = () => {
  const [toggle, setToggle] = useState(false);
  const [userError, setUserError] = useState(null);
  const toastId = useRef(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  let location = useLocation();
  let from = location.state?.from?.pathname || "/dashboard";

  const [signInWithEmailAndPassword, signInUser, signInLoading, signInError] = useSignInWithEmailAndPassword(auth);
  const [createUserWithEmailAndPassword, user, loading, error] = useCreateUserWithEmailAndPassword(auth);
  const [sendPasswordResetEmail, sending, resetPasswordError] = useSendPasswordResetEmail(auth);

  const formatDateToUTC = (date) => {
    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: 'numeric',
      timeZone: 'UTC',
      hour12: false,
    };
    return new Date(date).toLocaleString('en-GB', options).replace(',', '') + ' UTC';
  };

  const saveUserInfoToFirestore = async (authUser) => {
    try {
      if (!authUser) {
        console.error("authUser is null or undefined. Cannot save user info.");
        return;
      }

      const userDocRef = doc(db, 'users', authUser.uid);
      const userDoc = await getDoc(userDocRef);

      const nameFromEmail = authUser.email?.split('@')[0]?.replace(/\./g, ' ') || "Unknown User";

      const userInfo = {
        uid: authUser.uid,
        email: authUser.email || "No Email",
        photoURL: authUser.photoURL || '',
        lastLoginAt: authUser.metadata?.lastSignInTime
          ? authUser.metadata.lastSignInTime
          : null,
        creationTime: authUser.metadata?.creationTime
          ? authUser.metadata.creationTime
          : null,
        phoneNumber: authUser.phoneNumber || '',
        ...(userDoc.exists()
          ? {}
          : {
            name: authUser.displayName || nameFromEmail,
            user_role: 'client',
            platform: 'web_portal',
          }),
      };

      await setDoc(userDocRef, userInfo, { merge: true });
    } catch (error) {
      console.error("Error saving user info to Firestore:", error);
    }
  };

  const handleAuthStateChange = (authUser) => {
    if (authUser) {
      saveUserInfoToFirestore(authUser);
      setUserError("");
      // navigate(from, { replace: true });
      navigate('/dashboard', { replace: true });
    }
  };

  const signUp = async (e) => {
    e.preventDefault();

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      toast.error('Please enter a valid email address.');
      return;
    }

    if (!password || password.length < 6) {
      toast.error('Password must be at least 6 characters long.');
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(email, password);

      if (userCredential.user) {
        await sendEmailVerification(userCredential.user);
        await saveUserInfoToFirestore(userCredential.user);
        await auth.signOut();

        toast.info('Verification email sent! Please check your inbox.',
          {
            toastId: 'Signing',
            className: 'red-toast',
            icon: <FontAwesomeIcon icon={faCircleCheck} bounce size="sm" style={{ color: "red", }} />,
            progressClassName: 'red-progress-bar',
          }
        );
      }
    } catch (error) {
      console.error('Error signing up:', error);
      toast.error('Failed to sign up. Please check your credentials or if the email is already in use.');
    }
  };


  const signIn = async (e) => {
    e.preventDefault();
    if (!email || !password) return;

    try {
      const userCredential = await signInWithEmailAndPassword(email, password);
      const user = userCredential.user;

      if (user.emailVerified || user.email === 'martin&co@firechecked.co.uk'
        || user.email === 'eastons@firechecked.co.uk' || user.email === 'pearce@firechecked.co.uk' ||
        user.email === 'vfm@firechecked.co.uk' ||
        user.email === 'james@firechecked.co.uk'

      ) {
        handleAuthStateChange(user);
      } else {
        await auth.signOut();
        toast.error('Please verify your email before logging in.');
      }
    } catch (error) {
      console.error('Error signing in:', error);

      if (error.code === 'auth/user-not-found') {
        toast.error('User does not exist. Would you like to sign up?');
      } else if (error.code === 'auth/wrong-password') {
        toast.error('Incorrect password. Please try again.');
      } else {
        toast.error('Failed to sign in. Please check your credentials.');
      }
    }
  };

  const resetpassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.info('Enter Email Address...', {
        toastId: 'EmailEA',
        className: 'red-toast',
        icon: <FontAwesomeIcon icon={faInfoCircle} bounce size="sm" style={{ color: "red", }} />,
        progressClassName: 'red-progress-bar',
      });
    } else {
      try {
        await sendPasswordResetEmail(email);
        toast.info('Sending Email...', {
          toastId: 'Email',
          className: 'red-toast',
          icon: <FontAwesomeIcon icon={faCircleCheck} bounce size="sm" style={{ color: "red", }} />,
          progressClassName: 'red-progress-bar',
        });
      } catch (error) {
        console.error('error', error);
        if (error.code === 'auth/user-not-found') {
          toast.info('User doesn\'t exist...', {
            toastId: 'UserDE',
            className: 'red-toast',
            icon: <FontAwesomeIcon icon={faInfoCircle} bounce size="sm" style={{ color: "red", }} />,
            progressClassName: 'red-progress-bar',
          });
        } else {
          console.error('Error sending password reset email:', error);
          toast.error('Failed to send password reset email', {
            toastId: 'ResetError'
          });
        }
      }
    }
  };

  if (signInLoading || loading || sending) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <>
      <div className="loginPage">
        <div className="loginWrapper">
          <div className="loginCard">
            <div className="loginBrand">
              <div>
                <img
                  src={logo}
                  alt="logo"
                  className="logo"
                  style={{ maxWidth: "30px" }}
                />
              </div>

              <div className="loginBrandName">
                FireChecked<span>.co.uk</span>
              </div>
            </div>

            <div className="loginHeader">
              <h1 className="loginTitle">
                {toggle ? "Create your account" : "Welcome back"}
              </h1>

              <p className="loginSubtitle">
                {toggle
                  ? "Create your FireChecked.co.uk account"
                  : "Sign in to continue to FireChecked.co.uk"}
              </p>
            </div>

            <form onSubmit={toggle ? signUp : signIn}>
              <div className="input-group mb-3">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faEnvelopeSquare} />
                </span>

                <input
                  type="email"
                  required
                  className="form-control"
                  placeholder="Email address"
                  aria-label="Email address"
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="input-group mb-3">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faKey} />
                </span>

                <input
                  type="password"
                  required
                  className="form-control"
                  placeholder="Password"
                  aria-label="Password"
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              {userError && (
                <div className="loginError">
                  {userError}
                </div>
              )}

              <button className="signInButton" type="submit">
                {toggle ? "Create Account" : "Sign In"}
              </button>

              <div className="loginActions">
                {toggle ? (
                  <p>
                    Already have an account?{" "}
                    <Link
                      className="red-link"
                      onClick={() => setToggle(false)}
                    >
                      Sign in
                    </Link>
                  </p>
                ) : (
                  <>
                    <p>
                      Forgot your password?{" "}
                      <Link
                        className="red-link"
                        onClick={resetpassword}
                        to=""
                      >
                        Reset password
                      </Link>
                    </p>

                    <p>
                      New to FireChecked.co.uk?{" "}
                      <Link
                        className="red-link"
                        onClick={() => setToggle(true)}
                      >
                        Create an account
                      </Link>
                    </p>
                  </>
                )}
              </div>

              <ToastContainer autoClose={1000} />
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
